import { Send, Bot, Settings, BarChart3, Activity, Upload, FileText, Shield, Zap, History, Sparkles, X, GripVertical, MessageCircle, AlertCircle, Plus, Loader2, Mail, Link2, RefreshCw, Trash2, Calculator, Eye, HelpCircle, Phone, PlayCircle, ChevronRight } from "lucide-react";
import { AnimatedBotIcon } from "@/app/_module/components/ui/bot";
import { unifiedAIService } from "@/app/_module/services/unifiedAIService";
// Shell chat avatar: the desktop-brand InsuraBot icon with the moving eyes
// (same one the bottom nav uses) — replaces the old generic bot glyph there.
import { AnimatedInsuraBotIcon } from "@/components/mobile/AnimatedInsuraBotIcon";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";

import { useToast } from "@/app/_module/hooks/use-toast";
import { useUnifiedAI } from "@/app/_module/hooks/useUnifiedAI";
import type { ChatMessage, Carrier } from "@/app/_module/types/ai-unified";
// Shared lead-field parsers — height/weight are VARCHAR on the lead row.
import {
  parseLeadHeight,
  parseLeadWeight,
} from "@/app/(authenticated)/quote-enroll/_modules/components/QuoteForm/leadAdapter";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getAdminAccess, getAIContextForUser } from "@/app/_module/utils/admin-access";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { isDirect130Host } from "@/app/_module/utils/direct130Host";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { DocumentRequestButton, parseAIResponseForMissingCarriers } from "./DocumentRequestButton";
import { RequestCarrierButton } from "./RequestCarrierButton";
import { Label } from "./ui/label";
import { UnderwritingQuoteTable } from "./UnderwritingQuoteTable";
import InsuraBotCardRenderer from "./insurabot-cards/InsuraBotCardRenderer";
import { useQuotes } from "@/app/_module/hooks/useQuoteService";
import { oauthConnectionService } from "../services/oauthConnectionService";
import type { EmailOAuthConnection } from "../types/email-oauth";
import { InsuraHelpPanel } from "./insura-help/InsuraHelpPanel";
import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";

interface UnifiedInsuraBotProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enableAdminFeatures?: boolean;
  defaultContext?: 'general' | 'underwriting' | 'resources' | 'quotes';
  contextMetadata?: Record<string, any>; // Additional metadata for context
  // Mirror this instance's open state into UnifiedDialerContext
  // (isInsuraBotOpen). ⚠️ The layout's nav instance RENDERS from that same
  // flag, so any secondary instance (lead-details quote flow) that mirrors
  // `true` drags the nav instance open on top of itself — pass false there.
  syncOpenToContext?: boolean;
}

// SVG icons for status indicators
const STATUS_ICONS = {
  yes: '<svg class="status-icon status-yes" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>',
  no: '<svg class="status-icon status-no" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>',
  maybe: '<svg class="status-icon status-maybe" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>',
  star: '<svg class="status-icon status-star" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>',
};

// Function to replace status emojis with SVG icons
const replaceStatusEmojis = (text: string): string => {
  return text
    .replace(/✅\s*(Yes|Accept|ACCEPT)/gi, `${STATUS_ICONS.yes}<span class="status-text-yes">Yes</span>`)
    .replace(/❌\s*(No|Decline|DECLINE)/gi, `${STATUS_ICONS.no}<span class="status-text-no">No</span>`)
    .replace(/⚠️\s*(Maybe|Conditional|CONDITIONAL)/gi, `${STATUS_ICONS.maybe}<span class="status-text-maybe">Maybe</span>`)
    .replace(/★/g, STATUS_ICONS.star)
    .replace(/✅/g, STATUS_ICONS.yes)
    .replace(/❌/g, STATUS_ICONS.no)
    .replace(/⚠️/g, STATUS_ICONS.maybe);
};

// Function to clean AI response formatting and convert markdown tables to HTML.
// When `stripTables` is true, markdown tables are REMOVED entirely instead
// of being rendered — used when the structured Quote Comparison card below
// already shows the same data and we don't want a duplicate (one small, one
// big). The surrounding intro/footer text is preserved.
const cleanAIResponse = (rawText: string, options?: { stripTables?: boolean }): string => {
  if (!rawText) return '';
  const stripTables = options?.stripTables === true;

  let cleanText = rawText
    .replace(/\*{3,}/g, '') // Remove triple+ asterisks
    .replace(/\*{2}([^*]+?)\*{2}/g, '<strong>$1</strong>') // Bold: **text** -> <strong>
    .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>') // Italic: *text* -> <em>
    .replace(/#{1,3}\s*(.+)/g, '<p class="ai-heading">$1</p>') // Headers -> styled heading
    .replace(/`([^`]+?)`/g, '<code class="ai-code">$1</code>') // Inline code -> styled code
    .replace(/```[\s\S]*?```/g, (m) => `<pre class="ai-codeblock">${m.replace(/```\w*\n?/g, '').trim()}</pre>`) // Code blocks
    .replace(/_{2,}/g, '') // Remove multiple underscores
    .replace(/~{2,}/g, '') // Remove multiple tildes
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines
    .trim();

  // Enhance bullet lists with styled markers
  cleanText = cleanText.replace(/^[•\-]\s+(.+)/gm, '<div class="ai-list-item"><span class="ai-list-dot"></span><span>$1</span></div>');

  // Highlight numbers that look like scores (standalone 2-3 digit numbers)
  cleanText = cleanText.replace(/\b(\d{2,3})\/100\b/g, '<span class="ai-score">$1</span>/100');
  cleanText = cleanText.replace(/\b(\d{2,3})%\b/g, '<span class="ai-score">$1</span>%');

  // Highlight dollar amounts
  cleanText = cleanText.replace(/\$(\d[\d,]*\.?\d*)/g, '<span class="ai-money">$$$1</span>');

  // Style links
  cleanText = cleanText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="ai-link" target="_blank" rel="noopener">$1</a>');

  // Helper: build HTML table from headers + rows arrays
  const buildHtmlTable = (headers: string[], rows: string[][]): string => {
    let html = '<div class="ai-table-wrapper"><table class="ai-table">';
    html += '<thead><tr>';
    headers.forEach((h: string) => {
      html += `<th>${h}</th>`;
    });
    html += '</tr></thead>';
    html += '<tbody>';
    rows.forEach((row: string[]) => {
      html += '<tr>';
      row.forEach((cell: string) => {
        const processedCell = replaceStatusEmojis(cell);
        html += `<td>${processedCell}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    return html;
  };

  // Convert markdown pipe tables to HTML tables — OR strip them entirely
  // when the structured Quote Comparison card will render the same data
  // (avoids the small-table-on-top, big-card-below duplicate).
  const tableRegex = /\|(.+)\|\n\|[-:\s|]+\|\n((?:\|.+\|\n?)+)/g;
  if (stripTables) {
    cleanText = cleanText.replace(tableRegex, '');
  } else {
    cleanText = cleanText.replace(tableRegex, (match, headerRow, bodyRows) => {
      const headers = headerRow.split('|').map((h: string) => h.trim()).filter((h: string) => h);
      const rows = bodyRows.trim().split('\n').map((row: string) => {
        return row.split('|').map((cell: string) => cell.trim()).filter((cell: string) => cell);
      });
      return buildHtmlTable(headers, rows);
    });
  }

  // Convert tab-separated tables to HTML tables (AI sometimes outputs tabs instead of pipes)
  const lines = cleanText.split('\n');
  let i = 0;
  const processedParts: string[] = [];
  while (i < lines.length) {
    const line = lines[i];
    // Detect tab-separated header: at least 2 tabs on this line and the next line also has tabs
    if (line.includes('\t') && i + 1 < lines.length && lines[i + 1].includes('\t')) {
      const headers = line.split('\t').map((h: string) => h.trim()).filter((h: string) => h);
      if (headers.length >= 2) {
        const tableRows: string[][] = [];
        let j = i + 1;
        // Check if next line is a separator (---) row, skip it
        if (lines[j] && /^[\t\s-|:]+$/.test(lines[j])) j++;
        // Collect body rows
        while (j < lines.length && lines[j].includes('\t')) {
          const cells = lines[j].split('\t').map((c: string) => c.trim()).filter((c: string) => c);
          if (cells.length >= 2) {
            tableRows.push(cells);
            j++;
          } else {
            break;
          }
        }
        if (tableRows.length > 0) {
          // When stripTables is on, drop the table entirely instead of
          // rendering it as HTML — the card below handles it.
          if (!stripTables) {
            processedParts.push(buildHtmlTable(headers, tableRows));
          }
          i = j;
          continue;
        }
      }
    }
    processedParts.push(line);
    i++;
  }
  cleanText = processedParts.join('\n');

  // Also replace emojis in non-table text
  cleanText = replaceStatusEmojis(cleanText);

  return cleanText;
};

interface SampleQuestion {
  id: string;
  question: string;
  category: string;
}

interface ChatSession {
  sessionId: string;
  contextType: string;
  createdAt: string;
  lastMessageAt?: string;
  messageCount?: number;
  firstMessagePreview?: string;
}

// Compact relative time for the History tab ("just now", "5m", "3h", "2d",
// then falls back to a short date) so rows stay clean and scannable.
function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Request type for carrier requests
type CarrierRequestType = "add_carrier" | "underwriting_data";

// Helper to detect if AI response contains underwriting tier information
const hasUnderwritingTiers = (response: string): boolean => {
  if (!response) return false;
  const tierPatterns = [
    /PREFERRED\s*(?:\/\s*LEVEL)?(?:\s*\(.*?\))?:/i,
    /STANDARD\s*(?:\(.*?\))?:/i,
    /GRADED\s*(?:\(.*?\))?:/i,
    /MODIFIED\s*(?:\(.*?\))?:/i,
  ];
  return tierPatterns.some(pattern => pattern.test(response));
};

// Helper to detect if AI response contains quote/price information (from Compulife)
const hasQuoteResults = (response: string): boolean => {
  if (!response) return false;
  // Detect quote tables or price mentions from InsuraBot
  const quotePatterns = [
    /\|\s*Carrier\s*\|.*\|\s*Monthly\s*\|/i, // Table header with Carrier and Monthly
    /\|\s*Carrier\s*\|.*\|\s*Mo\s*\|/i, // Compact table header
    /Insurance Quotes for/i, // Quote section header
    /quotes?\s+found/i, // "X quotes found"
    /\$\d+\.?\d*\s*\/?\s*mo(?:nth)?/i, // Price like "$45/month" or "$45.00 mo"
  ];
  return quotePatterns.some(pattern => pattern.test(response));
};

// Helper to check if response should show the underwriting/quote table.
// Fires on either underwriting tiers OR a quote table — the card is the
// preferred render (interactive sorting, suggestion badges, etc.) so we
// show it for both. The markdown table above still renders on top so
// the user gets both views: free-form text + the structured card.
const shouldShowUnderwritingQuoteTable = (response: string): boolean => {
  return hasUnderwritingTiers(response) || hasQuoteResults(response);
};

export const UnifiedInsuraBot = ({
  open,
  onOpenChange,
  enableAdminFeatures = false,
  defaultContext = 'general',
  contextMetadata,
  syncOpenToContext = true
}: UnifiedInsuraBotProps) => {
  // ⚠️ Must sit above every early return in this component (`if (!open) return
  // null` further down) — a hook called conditionally changes hook order
  // between renders and React throws.
  const pathname = usePathname();
  const isMobileShell = !!pathname?.startsWith('/m');

  const { toast } = useToast();
  const { user } = useUserContext();
  const { branding } = useBranding();
  const enrollmentFloater = useEnrollmentFloater();

  // eApp launch links the AI prints inline in its message text (e.g.
  // eapp.techf.com?applicationLoginToken=...) must NOT open browser tabs —
  // route them through the enrollment surfaces like every other launch
  // point: techf.com → embedded iframe, carrier domains → popup window.
  // Delegated handler because message HTML comes from
  // dangerouslySetInnerHTML (no per-anchor React handlers possible).
  const handleChatLinkClick = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement)?.closest?.("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    // SECURITY: decide by the PARSED hostname, never by substring/prefix
    // regex on the raw href — `techf.com.evil.com` must not reach the
    // iframe, and an arbitrary URL must never get a popup launch (our
    // popups keep window.opener for close-polling, which a hostile page
    // could abuse; default anchors here are rel=noopener tabs). Scope:
    // ONLY exact techf.com hosts are intercepted → embedded iframe.
    // Everything else keeps the anchor's default behavior.
    let parsed: URL;
    try {
      parsed = new URL(href, window.location.origin);
    } catch {
      return;
    }
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return;
    const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
    const isTechf = host === "techf.com" || host.endsWith(".techf.com");
    if (!isTechf) return;
    e.preventDefault();
    enrollmentFloater.launch({
      launchUrl: parsed.toString(),
      launchMode: "iframe",
    });
  };
  const { isViewerMode } = useTrialStatus();
  const router = useRouter();
  const { isMinimized: isDialerMinimized, isInsuraHelpFromDialerOpen, setInsuraHelpFromDialerOpen, setInsuraBotOpen, setInsuraBotRect, maximizeWidget: maximizeDialer } = useUnifiedDialer();
  const { activeSmsConversation } = useSmsConversation();
  const [input, setInput] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  // Default to expanded — the compact size made the AI's response tables
  // and quote cards squeeze awkwardly. The expand toggle is gone now
  // (replaced by the transparency toggle) but the widget still opens at
  // the expanded dimensions and the user can manually resize via the
  // top-left corner handle.
  const [isExpanded, setIsExpanded] = useState(true);

  // Transparency toggle — same 3-step cycle as ScriptPanel + Enrollment
  // Floater. Click the Eye button to cycle Opaque → Translucent → See-through
  // → back to Opaque. Hover auto-restores to fully opaque so the widget is
  // always readable while you're actually using it.
  const TRANSLUCENCY_STEPS = [
    { label: 'Opaque', idle: 1.0 },
    { label: 'Translucent', idle: 0.65 },
    { label: 'See-through', idle: 0.35 },
  ];
  const [translucencyIdx, setTranslucencyIdx] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const saved = window.localStorage.getItem('insurabot-translucency');
    const n = Number(saved);
    return Number.isFinite(n) && n >= 0 && n < TRANSLUCENCY_STEPS.length ? n : 0;
  });
  const [isWidgetHovered, setIsWidgetHovered] = useState(false);
  const idleOpacity = TRANSLUCENCY_STEPS[translucencyIdx]?.idle ?? 1;
  const currentOpacity = isWidgetHovered || idleOpacity === 1 ? 1 : idleOpacity;

  const cycleTranslucency = useCallback(() => {
    setTranslucencyIdx((prev) => {
      const next = (prev + 1) % TRANSLUCENCY_STEPS.length;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('insurabot-translucency', String(next));
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag & resize state — position stored as right/bottom from viewport edges
  const DEFAULT_WIDTH = 500;
  const DEFAULT_HEIGHT_VH = 65;
  const EXPANDED_WIDTH = 800;
  const EXPANDED_HEIGHT_VH = 90;
  const MIN_WIDTH = 320; // shrunk from 380 — iPhone 14 mini is 375px wide
  const MIN_HEIGHT = 400;
  // Leave a small gutter so the widget never paints over the viewport edge
  // (which made the resize handle un-grabbable on small screens).
  const VIEWPORT_GUTTER = 16;

  // Clamp helpers — used everywhere we touch widget size so a tiny screen
  // can't end up with a widget that's wider/taller than the viewport.
  const clampWidth = (w: number) =>
    typeof window === 'undefined'
      ? w
      : Math.max(MIN_WIDTH, Math.min(w, window.innerWidth - VIEWPORT_GUTTER));
  const clampHeight = (h: number) =>
    typeof window === 'undefined'
      ? h
      : Math.max(MIN_HEIGHT, Math.min(h, window.innerHeight - VIEWPORT_GUTTER));

  const [widgetPos, setWidgetPos] = useState({ right: 32, bottom: 8 });
  const [widgetSize, setWidgetSize] = useState(() => {
    // Open expanded by default — matches the isExpanded=true default above.
    // SSR fallback uses the expanded width too so there's no flash of
    // small layout on first paint.
    if (typeof window === 'undefined') return { width: EXPANDED_WIDTH, height: 900 };
    return {
      width: clampWidth(EXPANDED_WIDTH),
      height: clampHeight(Math.round(window.innerHeight * EXPANDED_HEIGHT_VH / 100)),
    };
  });

  // Track viewport so the widget shrinks live as you resize the window
  // (or rotate a tablet). Without this, opening at 1920px wide then
  // dragging the browser to 600px leaves the widget partially off-screen.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setWidgetSize((prev) => ({
        width: clampWidth(prev.width),
        height: clampHeight(prev.height),
      }));
      // Also clamp position so the widget never floats off-screen.
      setWidgetPos((prev) => ({
        right: Math.max(0, Math.min(prev.right, window.innerWidth - MIN_WIDTH)),
        bottom: Math.max(0, Math.min(prev.bottom, window.innerHeight - MIN_HEIGHT)),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dragRef = useRef<{ startX: number; startY: number; startRight: number; startBottom: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const widgetContainerRef = useRef<HTMLDivElement>(null);

  // Sync rect to context so dialer/InsuraHelp buttons can follow
  const updateRect = useCallback((right: number, bottom: number, width: number, height: number) => {
    setInsuraBotRect({ right, bottom, width, height });
  }, [setInsuraBotRect]);

  useEffect(() => {
    if (open) {
      updateRect(widgetPos.right, widgetPos.bottom, widgetSize.width, widgetSize.height);
    } else {
      setInsuraBotRect(null);
    }
  }, [open, widgetPos, widgetSize, updateRect, setInsuraBotRect]);

  // Expand button removed; resizing now happens via the top-left corner
  // resize handle. Widget opens at EXPANDED dimensions by default
  // (see widgetSize initializer above) — no toggle needed.

  // Drag handlers.
  //
  // Previously the help-button / floating phone button lagged one frame
  // behind the widget on drag because the shared `insuraBotRect` was only
  // synced via a useEffect that fires AFTER React commits the widget's
  // new position — so the widget paints at frame N, the help button
  // catches up at frame N+1. Now we update the shared rect IN the
  // mousemove handler, in the same batch as setWidgetPos, so both
  // consumers re-render together and the help button sticks to the
  // widget like glue.
  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startRight: widgetPos.right, startBottom: widgetPos.bottom };
    const onMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = dragRef.current.startX - ev.clientX;
      const dy = dragRef.current.startY - ev.clientY;
      const newRight = Math.max(0, dragRef.current.startRight + dx);
      const newBottom = Math.max(0, dragRef.current.startBottom + dy);
      setWidgetPos({ right: newRight, bottom: newBottom });
      // Sync the shared rect in the same batch — no useEffect round trip.
      // React 18 automatic batching collapses both setStates into one
      // commit so the help button follows the widget pixel-for-pixel.
      setInsuraBotRect({
        right: newRight,
        bottom: newBottom,
        width: widgetSize.width,
        height: widgetSize.height,
      });
    };
    const onMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widgetPos, widgetSize, setInsuraBotRect]);

  // Resize handlers (top-left corner) — same in-batch rect sync as drag.
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startWidth: widgetSize.width, startHeight: widgetSize.height };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = resizeRef.current.startX - ev.clientX; // dragging left = increase width
      const dy = resizeRef.current.startY - ev.clientY; // dragging up = increase height
      // Clamp through the helpers so manual resize can't exceed the viewport
      // (previously you could drag past the viewport edge and lose the handle).
      const newWidth = clampWidth(resizeRef.current.startWidth + dx);
      const newHeight = clampHeight(resizeRef.current.startHeight + dy);
      setWidgetSize({ width: newWidth, height: newHeight });
      // Same in-batch rect sync as the drag handler.
      setInsuraBotRect({
        right: widgetPos.right,
        bottom: widgetPos.bottom,
        width: newWidth,
        height: newHeight,
      });
    };
    const onMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widgetSize, widgetPos, setInsuraBotRect]);

  // ── Shell floating window (same pattern as ScriptPanel) ──
  // On /m the widget starts on the .mobile-insurabot class geometry (full
  // sheet). The FIRST touch-drag or touch-resize measures the live rect,
  // drops the class, and switches to inline fixed top/left/width/height —
  // the class can't be kept because its !important insets beat inline
  // styles. Handlers are attached non-passively so preventDefault actually
  // stops the page from scrolling under the drag (same iOS lesson as
  // PullToRefresh).
  const [shellRect, setShellRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const shellTouchRef = useRef<{ mode: 'drag' | 'resize'; startX: number; startY: number; start: { top: number; left: number; width: number; height: number } } | null>(null);
  const onShellTouchStart = useCallback((mode: 'drag' | 'resize') => (e: React.TouchEvent) => {
    if (!isMobileShell) return;
    const t = e.touches[0];
    if (!t) return;
    let start = shellRect;
    if (!start) {
      const el = widgetContainerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      start = { top: r.top, left: r.left, width: r.width, height: r.height };
    }
    shellTouchRef.current = { mode, startX: t.clientX, startY: t.clientY, start };
    const onMove = (ev: TouchEvent) => {
      const ref = shellTouchRef.current;
      const tt = ev.touches[0];
      if (!ref || !tt) return;
      ev.preventDefault();
      const dx = tt.clientX - ref.startX;
      const dy = tt.clientY - ref.startY;
      if (ref.mode === 'drag') {
        // keep at least a grabbable sliver on screen in every direction
        const top = Math.max(4, Math.min(ref.start.top + dy, window.innerHeight - 80));
        const left = Math.max(60 - ref.start.width, Math.min(ref.start.left + dx, window.innerWidth - 60));
        setShellRect({ ...ref.start, top, left });
      } else {
        const width = Math.max(300, Math.min(ref.start.width + dx, window.innerWidth - ref.start.left - 4));
        const height = Math.max(340, Math.min(ref.start.height + dy, window.innerHeight - ref.start.top - 4));
        setShellRect({ ...ref.start, width, height });
      }
    };
    const onEnd = () => {
      shellTouchRef.current = null;
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
  }, [isMobileShell, shellRect]);

  const [showHistory, setShowHistory] = useState(false);
  const [sampleQuestions, setSampleQuestions] = useState<SampleQuestion[]>([]);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("chat");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Request Carrier Modal State
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestType, setRequestType] = useState<CarrierRequestType>("add_carrier");
  const [newCarrierName, setNewCarrierName] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  // PDF Upload State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  // OAuth Email Connection State
  const [oauthConnection, setOauthConnection] = useState<EmailOAuthConnection | null>(null);
  const [checkingOAuthConnection, setCheckingOAuthConnection] = useState(false);

  // Use the unified AI hook
  const {
    messages,
    sessionId,
    contextType,
    carrierSlug,
    carriers,
    isLoading,
    isUploading,
    uploadProgress,
    analytics,
    sendMessage,
    setContextType,
    setCarrierSlug,
    uploadDocument,
    clearMessages,
    addAssistantCardMessage,
    loadCarriers,
    loadAnalytics,
    loadChatHistory,
    adoptSession,
    isUnderwritingQuestion,
    extractCarrierFromMessage
  } = useUnifiedAI({
    initialContextType: defaultContext,
    autoDetectContext: true,
    contextMetadata
  });

  // Get quotes for lead if we have a leadId in context (for combining with underwriting)
  const leadId = contextMetadata?.leadId || '';
  const { data: quotes = [], isLoading: isLoadingQuotes } = useQuotes(leadId);

  // Load carriers, analytics, and sample questions on mount
  useEffect(() => {
    if (open && !isViewerMode) {
      loadCarriers();
      loadSampleQuestions();
      if (enableAdminFeatures) {
        loadAnalytics();
      }
    }
    // If viewer mode is active, switch to chat tab
    if (isViewerMode && activeTab !== 'chat') {
      setActiveTab('chat');
    }
  }, [open, enableAdminFeatures, loadCarriers, loadAnalytics, isViewerMode, activeTab]);

  // Track if we should show the quote suggestion
  const [showQuoteSuggestion, setShowQuoteSuggestion] = useState(false);
  // ── One visible InsuraBot at a time ──
  // The app mounts SEVERAL instances (the layout's nav-controlled one, plus a
  // per-lead quote instance). Both portal fullscreen to document.body, so if
  // two are open the later-stacked EMPTY one paints over the quote form —
  // "press Quote, see plain chat, form only appears after closing it".
  // Opening any instance broadcasts; every other open instance closes itself.
  const instanceIdRef = useRef(`ib-${Math.random().toString(36).slice(2)}`);
  useEffect(() => {
    if (!open) return;
    try {
      window.dispatchEvent(new CustomEvent("insurabot:instance-opened", { detail: instanceIdRef.current }));
    } catch {}
  }, [open]);
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent).detail;
      if (open && id !== instanceIdRef.current) onOpenChange(false);
    };
    window.addEventListener("insurabot:instance-opened", handler);
    return () => window.removeEventListener("insurabot:instance-opened", handler);
  }, [open, onOpenChange]);

  // Guard against double-sending the initialMessage when the effect re-fires
  // (React strict mode in dev, or contextMetadata identity changes).
  const autoSentRef = useRef(false);

  // Inject the quote form card directly into the chat when the bot opens
  // with `autoSendInitial`. No AI roundtrip — we already know the agent
  // wants the form, so we render it as a synthetic assistant message. Fast,
  // resilient to OpenAI quota / outages, no chat ping-pong visible.
  //
  // Legacy `startQuoting` path (without `autoSendInitial`) still shows the
  // tap-to-confirm suggestion bubble — lead-detail flows depend on it.
  useEffect(() => {
    if (!open) {
      setShowQuoteSuggestion(false);
      autoSentRef.current = false;
      return;
    }
    if (isViewerMode) return;
    if (messages.length !== 0) return;
    if (!contextMetadata?.startQuoting) return;

    if (contextMetadata?.autoSendInitial) {
      if (autoSentRef.current) return;
      autoSentRef.current = true;
      const preset = {
        leadId: contextMetadata.leadId || null,
        leadName: contextMetadata.leadName || null,
        birthDate: contextMetadata.dob || contextMetadata.birthDate || null,
        gender: contextMetadata.gender || null,
        state: contextMetadata.state || null,
        zipCode: contextMetadata.zipCode || null,
        // `lead.height` / `lead.weight` are VARCHAR on the lead row — the API
        // hands us strings like "5.11" (= 5'11", feet.inches) or "5'10\"" or
        // "175". The old `typeof === "number"` test was therefore false for
        // essentially every real lead, so the quote builder always opened with
        // Height and Weight blank. Use the same parsers the forms and
        // leadAdapter use so all three surfaces agree on what "5.11" means.
        heightInches: parseLeadHeight(contextMetadata.height),
        weightPounds: parseLeadWeight(contextMetadata.weight),
        hint: contextMetadata.leadName
          ? `Pre-filled what I had on file for ${contextMetadata.leadName} — fill in the rest and run.`
          : "Fill in the basics and run a multi-carrier quote.",
      };
      const greeting = contextMetadata.leadName
        ? `Here's a quick quote builder for ${contextMetadata.leadName} — every carrier you're appointed with will rank itself by price.`
        : "Here's a quote builder. Every carrier you're appointed with will rank itself by price.";
      const cards = [{ type: "techficient_quote_form" as const, data: preset }];
      addAssistantCardMessage(greeting, cards);

      // Persist this locally-injected exchange so it shows in History and can
      // be reopened with the form card intact. One session per lead per
      // browser session (cached id) — reopening the Quote tab adopts the
      // existing session instead of spawning a duplicate History row.
      const persistKey = contextMetadata.leadId
        ? `ib-quote-session-${contextMetadata.leadId}`
        : null;
      let cachedId: string | null = null;
      try { cachedId = persistKey ? sessionStorage.getItem(persistKey) : null; } catch {}
      if (cachedId) {
        adoptSession(cachedId);
      } else {
        unifiedAIService
          .saveCardSession({
            message:
              contextMetadata.initialMessage ||
              `Show me the quote form for ${contextMetadata.leadName || "this lead"}.`,
            response: greeting,
            contextType: contextType,
            cards,
            metadata: {
              quoteMode: true,
              leadId: contextMetadata.leadId || undefined,
              leadName: contextMetadata.leadName || undefined,
            },
          })
          .then((r) => {
            if (r?.sessionId) {
              adoptSession(r.sessionId);
              try { if (persistKey) sessionStorage.setItem(persistKey, r.sessionId); } catch {}
            }
          });
      }
      return;
    }

    if (contextMetadata?.initialMessage) {
      setShowQuoteSuggestion(true);
    }
  }, [
    open,
    isViewerMode,
    contextMetadata,
    messages.length,
    addAssistantCardMessage,
    adoptSession,
    contextType,
  ]);

  // Handle clicking the quote suggestion (legacy bubble path)
  const handleQuoteSuggestionClick = () => {
    if (contextMetadata?.initialMessage) {
      setShowQuoteSuggestion(false);
      sendMessage(contextMetadata.initialMessage);
    }
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    // Simple, reliable auto-scroll
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Load chat history only when explicitly requested via History tab
  // Removed auto-loading on mount to prevent session initialization errors
  // History will be loaded on-demand when user switches to History tab

  // Load sample questions
  const loadSampleQuestions = async () => {
    try {
      // Import dynamically to avoid circular dependencies
      const { default: supabaseAuthService } = await import('../services/supabaseAuthService');
      const { getApiUrl } = await import('../services/config');
      const { endpoint } = await import('../services/endpoints');

      // Get auth token
      const token = await supabaseAuthService.getAccessToken();

      if (!token) {

        return;
      }

      const response = await fetch(getApiUrl(endpoint.chatbot.sampleQuestions), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {

        return;
      }

      const data = await response.json();
      if (data.data?.questions) {
        setSampleQuestions(data.data.questions.slice(0, 4)); // Show only 4 questions
      }
    } catch (error) {

    }
  };

  // Load chat sessions
  // How many sessions to pull per page for the History tab's infinite scroll.
  const SESSIONS_PAGE_SIZE = 20;

  // Load a page of chat sessions. `append=false` (default) refreshes from the
  // top; `append=true` fetches the next page (offset = current count) and
  // appends — that's what powers the History tab's infinite scroll.
  const loadChatSessions = async (append = false) => {
    if (append) {
      // Guard against overlapping/exhausted paginated loads.
      if (loadingMoreSessions || loadingSessions || !hasMoreSessions) return;
      setLoadingMoreSessions(true);
    } else {
      setLoadingSessions(true);
    }
    try {
      // Import dynamically to avoid circular dependencies
      const { default: supabaseAuthService } = await import('../services/supabaseAuthService');
      const { getApiUrl } = await import('../services/config');
      const { endpoint } = await import('../services/endpoints');

      // Get auth token
      const token = await supabaseAuthService.getAccessToken();

      if (!token) {
        setLoadingSessions(false);
        setLoadingMoreSessions(false);
        return;
      }

      const offset = append ? chatSessions.length : 0;
      const response = await fetch(
        `${getApiUrl(endpoint.chatbot.getSessions)}?limit=${SESSIONS_PAGE_SIZE}&offset=${offset}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        setLoadingSessions(false);
        setLoadingMoreSessions(false);
        return;
      }

      const data = await response.json();
      const page: ChatSession[] = Array.isArray(data.data) ? data.data : [];
      if (append) {
        // De-dupe by sessionId in case pages overlap (new sessions shift offsets).
        const seen = new Set(chatSessions.map((s) => s.sessionId));
        const fresh = page.filter((s) => !seen.has(s.sessionId));
        setChatSessions((prev) => [...prev, ...fresh]);
        // Stop paging on a short page OR if we got nothing new (safety net for a
        // backend that ignores `offset` — prevents an endless no-op fetch loop).
        setHasMoreSessions(page.length === SESSIONS_PAGE_SIZE && fresh.length > 0);
      } else {
        setChatSessions(page);
        setHasMoreSessions(page.length === SESSIONS_PAGE_SIZE);
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to load conversation history",
        variant: "destructive",
      });
    } finally {
      setLoadingSessions(false);
      setLoadingMoreSessions(false);
    }
  };

  // Load conversation from history
  const loadConversation = async (sessionId: string) => {
    try {
      await loadChatHistory(sessionId);
      setActiveTab("chat");
      setShowHistory(false);
      toast({
        title: "Conversation loaded",
        description: "Previous chat history has been restored",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load conversation history",
        variant: "destructive",
      });
    }
  };

  // Check OAuth connection when modal opens
  useEffect(() => {
    if (isRequestModalOpen) {
      checkOAuthConnection();
    }
  }, [isRequestModalOpen]);

  const checkOAuthConnection = async () => {
    setCheckingOAuthConnection(true);
    try {
      const status = await oauthConnectionService.getConnectionStatus();
      const googleConnected = status.google?.status === "connected";
      const microsoftConnected = status.microsoft?.status === "connected";

      if (googleConnected) {
        setOauthConnection(status.google!);
      } else if (microsoftConnected) {
        setOauthConnection(status.microsoft!);
      } else {
        setOauthConnection(null);
      }
    } catch (error) {
      console.error("Failed to check OAuth connection:", error);
      setOauthConnection(null);
    } finally {
      setCheckingOAuthConnection(false);
    }
  };

  // File handling functions for carrier request
  const handleFileSelectForRequest = (file: File) => {
    // Validate file type (PDF only)
    if (file.type !== "application/pdf") {
      toast({
        title: "Invalid File Type",
        description: "Only PDF files are allowed.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 10MB.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsFileDragging(false);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsFileDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelectForRequest(file);
    }
  };

  const handleFileInputChangeForRequest = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelectForRequest(file);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileUploadInputRef.current) {
      fileUploadInputRef.current.value = "";
    }
  };

  const resetRequestForm = () => {
    setNewCarrierName("");
    setRequestReason("");
    setSelectedFile(null);
    setRequestType("add_carrier");
    setIsRequestModalOpen(false);
    if (fileUploadInputRef.current) {
      fileUploadInputRef.current.value = "";
    }
  };

  const handleConnectEmail = () => {
    // Close the InsuraBot and redirect to settings connections tab
    onOpenChange(false);
    router.push("/settings?tab=account&accountTab=connections");
  };

  const handleRequestCarrier = async () => {
    if (!newCarrierName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a carrier name.",
        variant: "destructive",
      });
      return;
    }

    if (!requestReason.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a reason for your request.",
        variant: "destructive",
      });
      return;
    }

    // For underwriting data requests with PDF, require OAuth connection
    if (requestType === "underwriting_data" && selectedFile && !oauthConnection) {
      toast({
        title: "Email Connection Required",
        description: "Please connect your Gmail or Outlook account to send underwriting documents.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmittingRequest(true);

      // Import api dynamically
      const apiModule = await import("../services/api");
      const { getApiUrl } = await import("../services/config");
      const api = apiModule.default;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("carrierName", newCarrierName.trim());
      formData.append("requestReason", requestReason.trim());
      formData.append("requestType", requestType);

      // Add file if present (for underwriting data requests)
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      // Add OAuth provider info if connected (for sending via user's email)
      if (oauthConnection && selectedFile) {
        formData.append("useOAuth", "true");
        formData.append("oauthProvider", oauthConnection.provider);
      }

      await api.post({
        url: getApiUrl('/carrier-requests/with-attachment'),
        body: formData,
        auth: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const successMessage = requestType === "add_carrier"
        ? `Your request to add "${newCarrierName}" has been submitted successfully.`
        : `Your underwriting data request for "${newCarrierName}" has been submitted${selectedFile ? " with the attached PDF" : ""}.`;

      toast({
        title: "Request Submitted",
        description: successMessage,
      });

      // Reset form and close modal
      resetRequestForm();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to submit carrier request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Demo walkthrough — reuses the same YouTube video the onboarding tour uses.
  const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=3AFy47rWac0';
  // The video is InsuraCentral's own, so Direct 130's address never offers it.
  const demoOffered = !isDirect130Host();
  const isDemoRequest = (msg: string) =>
    demoOffered && /\b(demo|walk ?through|show me (a |the )?(demo|around|app)|product tour|watch (the )?(demo|video))\b/i.test(msg);
  const showDemo = () => {
    // Embed the walkthrough INLINE (a playable card right here in the chat),
    // not a link that opens a new tab.
    addAssistantCardMessage(
      `Here's a full walkthrough of InsuraCentral — play it right here 👇\n\nAsk me anything else once you've watched!`,
      [
        {
          type: "video",
          data: {
            url: DEMO_VIDEO_URL,
            title: "InsuraCentral — full walkthrough",
            durationLabel: "~13 min",
          },
        },
      ],
    );
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');

    // If they're asking for a demo, link the walkthrough video instead of the AI.
    if (isDemoRequest(message)) {
      showDemo();
      return;
    }

    // Auto-detect if this is an underwriting question
    if (isUnderwritingQuestion(message)) {
      const detectedCarrier = extractCarrierFromMessage(message);
      if (detectedCarrier && !carrierSlug) {
        setCarrierSlug(detectedCarrier);
        setContextType('underwriting');
        toast({
          title: "Auto-detected underwriting question",
          description: `Switched to underwriting mode for ${detectedCarrier}`,
        });
      }
    }

    await sendMessage(message);
  };

  // Shell-style message history navigation.
  //
  // historyIndex semantics:
  //   -1     → not navigating (typing fresh input)
  //   0..n   → currently showing the Nth-most-recent user message
  //            (0 = most recent, n = oldest)
  //
  // pendingInputRef preserves whatever the user was typing BEFORE they
  // pressed Up. If they Down past the newest history entry, we restore
  // it. Standard terminal-like UX (bash, fish, zsh, devtools console).
  const [historyIndex, setHistoryIndex] = useState(-1);
  const pendingInputRef = useRef<string>('');

  // Build the list of user-sent messages in reverse chronological order.
  // Most recent at index 0 so "Up" walks BACK in time (index increases).
  //
  // ChatMessage shape (see types/ai-unified.ts): each row pairs the user's
  // `message` with the AI's `response`. We only care about the user side
  // for history nav.
  const userMessageHistory = useMemo(() => {
    return (messages || [])
      .filter((m: any) => typeof m?.message === 'string' && m.message.trim() !== '')
      .map((m: any) => m.message as string)
      .reverse();
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      // Reset history nav so the next session starts fresh.
      setHistoryIndex(-1);
      pendingInputRef.current = '';
      return;
    }

    // History navigation. Only fire when the textarea is "empty enough"
    // to be unambiguous OR when we're already navigating history — we
    // don't want to hijack arrow keys when the user is editing a
    // multi-line draft.
    const target = e.target as HTMLTextAreaElement;
    const caretAtStart = target.selectionStart === 0 && target.selectionEnd === 0;
    const caretAtEnd =
      target.selectionStart === target.value.length &&
      target.selectionEnd === target.value.length;
    const isSingleLine = !target.value.includes('\n');

    if (e.key === 'ArrowUp' && (caretAtStart || isSingleLine || historyIndex >= 0)) {
      if (userMessageHistory.length === 0) return;
      e.preventDefault();
      // Stash whatever they were typing before the first Up press.
      if (historyIndex === -1) {
        pendingInputRef.current = input;
      }
      const nextIdx = Math.min(historyIndex + 1, userMessageHistory.length - 1);
      setHistoryIndex(nextIdx);
      setInput(userMessageHistory[nextIdx]);
      // Caret to end after React applies the value.
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = inputRef.current.value.length;
          inputRef.current.selectionEnd = inputRef.current.value.length;
        }
      });
      return;
    }

    if (e.key === 'ArrowDown' && historyIndex >= 0 && (caretAtEnd || isSingleLine)) {
      e.preventDefault();
      const nextIdx = historyIndex - 1;
      if (nextIdx < 0) {
        // Walked past the newest — restore the original draft.
        setHistoryIndex(-1);
        setInput(pendingInputRef.current);
        pendingInputRef.current = '';
      } else {
        setHistoryIndex(nextIdx);
        setInput(userMessageHistory[nextIdx]);
      }
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = inputRef.current.value.length;
          inputRef.current.selectionEnd = inputRef.current.value.length;
        }
      });
      return;
    }

    // Any other typing / editing breaks history nav.
    if (historyIndex !== -1 && e.key.length === 1) {
      setHistoryIndex(-1);
      pendingInputRef.current = '';
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // No carrier selection required - n8n will auto-detect carrier from document
    try {
      await uploadDocument(file);
      toast({
        title: "Document uploaded",
        description: contextType === 'underwriting'
          ? "Document is being processed. Carrier will be auto-detected from the document."
          : "Document is being processed for general AI knowledge",
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload document",
        variant: "destructive",
      });
    }

    // Reset file input
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Check file type
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

      if (!validTypes.includes(fileExtension)) {
        toast({
          title: "Invalid file type",
          description: "Please upload PDF, DOC, or DOCX files only",
          variant: "destructive",
        });
        return;
      }

      await handleFileUpload(file);
    }
  };

  const getSourceIcon = (source: 'general_ai' | 'enhanced_underwriting') => {
    return source === 'enhanced_underwriting' ? Shield : Bot;
  };

  const getSourceColor = (source: 'general_ai' | 'enhanced_underwriting') => {
    return source === 'enhanced_underwriting' ? 'bg-green-100 text-green-800' : 'bg-primary/10 text-primary';
  };

  // Track previous InsuraHelp state to detect when it opens
  const prevInsuraHelpFromDialerOpen = useRef(isInsuraHelpFromDialerOpen);

  useEffect(() => {
    const wasOpen = prevInsuraHelpFromDialerOpen.current;
    const isNowOpen = isInsuraHelpFromDialerOpen;
    prevInsuraHelpFromDialerOpen.current = isNowOpen;

    // If InsuraHelp from dialer just opened (was false, now true) and InsuraBot is open, close InsuraBot
    if (!wasOpen && isNowOpen && open) {
      onOpenChange(false);
    }
    // If InsuraBot is open and InsuraHelp from dialer was already open (InsuraBot just opened), close InsuraHelp
    // This is handled by the InsuraBot opening logic - we set isInsuraHelpFromDialerOpen to false
  }, [isInsuraHelpFromDialerOpen, open, onOpenChange]);

  // When InsuraBot opens, close InsuraHelp panel in dialer
  const prevOpen = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpen.current;
    const isNowOpen = open;
    prevOpen.current = isNowOpen;

    // If InsuraBot just opened (was false, now true) and InsuraHelp from dialer is open, close it
    if (!wasOpen && isNowOpen && isInsuraHelpFromDialerOpen) {
      setInsuraHelpFromDialerOpen(false);
    }
  }, [open, isInsuraHelpFromDialerOpen, setInsuraHelpFromDialerOpen]);

  // Sync InsuraBot open state with context so other widgets can respond.
  // Secondary instances (syncOpenToContext=false) must NOT write here — the
  // layout's nav instance renders from this flag, so mirroring `true` from a
  // quote-flow instance opened an empty nav bot ON TOP of the quote form.
  useEffect(() => {
    if (!syncOpenToContext) return;
    setInsuraBotOpen(open);
  }, [open, setInsuraBotOpen, syncOpenToContext]);

  // Land on the Helper tab when asked (e.g. the SMS widget's help button, which
  // used to open the retired floating InsuraHelp "?" widget). Registered
  // unconditionally so it works even while the bot is closed.
  useEffect(() => {
    const handler = () => setActiveTab('insuracentral');
    window.addEventListener('insurabot:open-helper', handler);
    return () => window.removeEventListener('insurabot:open-helper', handler);
  }, []);

  // Note: InsuraBot can coexist with InsuraHelp widget when dialer is minimized
  // The positioning logic handles placing them side by side
  // Only the InsuraHelp panel FROM DIALER needs to close when InsuraBot opens (handled above)

  // If not open, don't render anything
  if (!open) return null;

  // Portal to document.body so the widget physically escapes any ancestor
  // stacking context or transformed containing block on the host page.
  // This is what guarantees the lead page's tab strip (or anything else)
  // can never paint on top of the widget no matter how the host nests it.
  if (typeof document === 'undefined') return null;

  // Full chat widget
  return createPortal(
    <div
      ref={widgetContainerRef}
      // Translucency matches GlobalEnrollmentFloater (the iframe floater):
      // element `opacity` fades the whole widget on idle, snaps back to full
      // opacity on hover. The portal above ensures opacity-fading doesn't let
      // the host page punch through — the widget is at document.body, so no
      // ancestor stacking context can paint on top.
      onMouseEnter={() => setIsWidgetHovered(true)}
      onMouseLeave={() => setIsWidgetHovered(false)}
      className={`z-[100] bg-white dark:bg-gray-900 border-2 border-primary rounded-lg shadow-2xl flex flex-col shadow-primary/50 transition-opacity duration-200 ${
        // insurabot-shell is a STYLE marker that stays on through drag/resize;
        // mobile-insurabot is GEOMETRY and is dropped once shellRect takes over.
        isMobileShell ? (shellRect ? "insurabot-shell" : "insurabot-shell mobile-insurabot") : "fixed"
      }`}
      // ⚠️ On a phone the desktop geometry is not merely cramped, it is
      // OFF-SCREEN: `right: 420` on a 390px viewport puts the whole widget past
      // the left edge, and the fixed px width/height ignore the notch and the
      // tab bar. `.mobile-insurabot` (globals.css) handles the insets, so on
      // the native shell we must NOT emit the inline box model at all —
      // inline styles beat classes.
      style={
        isMobileShell
          ? shellRect
            ? {
                // user has dragged/resized: inline fixed geometry takes over
                // (the class is dropped above — its !important insets would
                // beat these otherwise)
                position: 'fixed',
                top: shellRect.top,
                left: shellRect.left,
                width: shellRect.width,
                height: shellRect.height,
                borderRadius: 12,
                opacity: currentOpacity,
              }
            : { opacity: currentOpacity }
          : {
              right: !isDialerMinimized ? 420 : activeSmsConversation ? 420 : widgetPos.right,
              bottom: widgetPos.bottom,
              width: activeSmsConversation && isDialerMinimized ? Math.min(widgetSize.width, 380) : widgetSize.width,
              height: widgetSize.height,
              opacity: currentOpacity,
            }
      }
    >
      {/* Resize handle — top-left corner */}
      <div
        className="absolute -top-1 -left-1 w-4 h-4 cursor-nw-resize z-10 flex items-center justify-center"
        onMouseDown={onResizeStart}
        title="Resize"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground/50 -rotate-45" />
      </div>
      {/* Shell resize handle — bottom-right, finger-sized, touch-driven.
          Same corner ScriptPanel uses; dragging grows width/height. */}
      {isMobileShell && (
        <div
          className="absolute -bottom-0.5 -right-0.5 z-20 h-11 w-11"
          style={{ touchAction: 'none' }}
          onTouchStart={onShellTouchStart('resize')}
          aria-label="Resize"
        >
          {/* bracket hugs the card's rounded corner exactly */}
          <span className="absolute bottom-[3px] right-[3px] block h-4 w-4 rounded-br-[9px] border-b-[2.5px] border-r-[2.5px] border-primary/60" />
        </div>
      )}
      {/* Header — drag handle. Click-and-drag anywhere on the header
          background moves the widget. The action buttons on the right
          stop the event from propagating so clicks on them don't start
          a drag. */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onShellTouchStart('drag')}
        style={isMobileShell ? { touchAction: 'none' } : undefined}
        className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 dark:bg-gray-800 rounded-t-md cursor-grab active:cursor-grabbing select-none"
      >
        <div className="flex items-center space-x-2">
          <Image src={branding.logoUrl || branding.faviconUrl || "/favicon.png"} alt={branding.brandName || "InsuraBot"} width={20} height={20} className="rounded" unoptimized />
          <h3 className="font-semibold">InsuraBot AI</h3>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary hover:bg-primary/10">Beta</Badge>
        </div>
        <div className="flex items-center space-x-1" onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
          {enableAdminFeatures && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAnalytics(!showAnalytics)}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
          )}
          {/* Dialer shortcut — replaces the floating phone button when
              InsuraBot is open. Same action: opens the (minimized) dialer.
              NOT on the shell: there it opened the dialer sheet UNDER the
              bot without closing it, which read as a dead button. */}
          {isDialerMinimized && !isMobileShell && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => maximizeDialer()}
              title="Open dialer"
              aria-label="Open dialer"
              className="text-primary hover:text-primary/80"
            >
              <Phone className="h-4 w-4" />
            </Button>
          )}

          {/* Transparency toggle — cycles Opaque → Translucent → See-through.
              Same pattern as ScriptPanel + Enrollment Floater. Hovering the
              widget at any non-opaque level auto-restores to full opacity
              so it stays readable while you're using it. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={cycleTranslucency}
            title={`${TRANSLUCENCY_STEPS[translucencyIdx]?.label} — click to cycle`}
            aria-label={`Transparency: ${TRANSLUCENCY_STEPS[translucencyIdx]?.label}. Click to cycle.`}
            className={translucencyIdx === 0 ? 'text-gray-500 hover:text-gray-900' : 'text-primary hover:text-primary/80'}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 px-4">
        <TabsList className="inline-flex w-full mt-2 mb-3 gap-1">
          <TabsTrigger
            value="chat"
            title="Client & underwriting help — quotes, carriers, tiers, documents"
            className="flex-1 h-full text-[11px] leading-tight text-center whitespace-normal px-1 text-blue-600 hover:bg-blue-600/10 data-[state=active]:text-blue-700 data-[state=active]:font-semibold"
          >
            Client Underwriting Support
          </TabsTrigger>
          <span aria-hidden className="w-px self-center h-5 bg-black" />
          <TabsTrigger
            value="insuracentral"
            title={`How to use ${branding.brandName} — platform features & walkthroughs`}
            className="flex-1 h-full text-[11px] leading-tight text-center whitespace-normal px-1 text-purple-600 hover:bg-purple-600/10 data-[state=active]:text-purple-700 data-[state=active]:font-semibold"
          >
            {branding.brandName} AI Support
          </TabsTrigger>
          {!isViewerMode && (
            <>
              <span aria-hidden className="w-px self-center h-5 bg-black" />
              <TabsTrigger
                value="history"
                onClick={() => loadChatSessions()}
                title="Your past chat sessions"
                className="flex-1 h-full text-[11px] leading-tight text-center whitespace-normal px-1 text-amber-600 hover:bg-amber-600/10 data-[state=active]:text-amber-700 data-[state=active]:font-semibold"
              >
                History
              </TabsTrigger>
              <span aria-hidden className="w-px self-center h-5 bg-black" />
              <TabsTrigger
                value="settings"
                title="InsuraBot settings"
                className="flex-1 h-full text-[11px] leading-tight text-center whitespace-normal px-1 text-emerald-600 hover:bg-emerald-600/10 data-[state=active]:text-emerald-700 data-[state=active]:font-semibold"
              >
                Settings
              </TabsTrigger>
            </>
          )}
        </TabsList>

          <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 mt-0 overflow-hidden">
            {/* Upload Progress */}
            {uploadProgress && (
              <Alert>
                <Activity className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>{uploadProgress.fileName}</span>
                      <span>{uploadProgress.status}</span>
                    </div>
                    {uploadProgress.status === 'uploading' && (
                      <Progress value={uploadProgress.progress} className="h-2" />
                    )}
                    {uploadProgress.error && (
                      <p className="text-red-600 text-sm">{uploadProgress.error}</p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Messages.
                `[&_[data-radix-scroll-area-viewport]>div]:!block` — Radix
                wraps viewport content in a `display: table` div, which
                sizes to the content's INTRINSIC width. Wide cards (the
                quote form / carrier results strip) would stretch the whole
                message column past the widget and get clipped. Forcing
                block keeps the column at widget width so inner rows
                horizontally scroll like they do on /quote-enroll. */}
            <ScrollArea
              className={`flex-1 relative [&_[data-radix-scroll-area-viewport]>div]:!block ${isDragging ? 'border-2 border-dashed border-primary bg-primary/5' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50 pointer-events-none">
                  <div className="text-center">
                    <Upload className="h-12 w-12 mx-auto mb-2 text-primary" />
                    <p className="text-lg font-medium">Drop your document here</p>
                    <p className="text-sm text-muted-foreground">PDF, DOC, or DOCX files</p>
                  </div>
                </div>
              )}
              {/* `min-w-0` so this container can shrink with the widget;
                  without it, a wide table inside one of the messages
                  would force the whole column to that table's intrinsic
                  width and freeze the layout at "too wide to readjust."
                  ─
                  Asymmetric padding: `pr-6` instead of `pr-4` to leave
                  room for the Radix ScrollArea scrollbar overlay (which
                  sits on top of the rightmost ~10-12px of content).
                  Without this, every bubble's right edge appears to be
                  clipped by the scrollbar. */}
              <div className="space-y-4 pl-4 pr-6 py-4 min-w-0">
                {/* Viewer Mode Block Message */}
                {isViewerMode && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-sm text-yellow-800">
                      <p className="font-medium">Chat is unavailable</p>
                      <p className="mt-1">
                        Your trial has expired or your subscription is inactive. Please upgrade to continue using InsuraBot.
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                
                {(!messages || messages.length === 0) && !isViewerMode && (
                  <div className="space-y-4">
                    {/* AI Greeting as Chat Bubble */}
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-lg px-3 py-2 max-w-[85%]">
                        <div className="flex items-start space-x-2">
                          {isMobileShell
                            ? <AnimatedInsuraBotIcon size={16} className="mt-0.5 flex-shrink-0" />
                            : <AnimatedBotIcon size={16} className="mt-0.5 flex-shrink-0 text-primary" />}
                          <div className="flex-1">
                            <p className="text-sm font-semibold tracking-tight">Hi, I&apos;m InsuraBot</p>
                            <p className="text-sm text-foreground mt-1.5 leading-relaxed">
                              {showQuoteSuggestion
                                ? "It looks like you'd like quotes for your client. Click below to begin."
                                : "I can help with underwriting, quotes, carrier info, documents, and more. Just ask!"
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quote Suggestion - Show when opened from Quotes tab */}
                    {showQuoteSuggestion && contextMetadata?.leadName && (
                      <div className="space-y-3 mt-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-primary px-1">
                          <Calculator className="h-4 w-4" />
                          <span>Get quotes for {contextMetadata.leadName}</span>
                        </div>
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-medium text-primary">Client Info:</p>
                          <div className="grid grid-cols-2 gap-1 text-xs text-primary">
                            <span className={!contextMetadata.state ? 'text-red-500' : ''}>
                              State: {contextMetadata.state || '⚠️ Missing'}
                            </span>
                            <span className={!contextMetadata.zipCode ? 'text-red-500' : ''}>
                              ZIP: {contextMetadata.zipCode || '⚠️ Missing'}
                            </span>
                            <span className={!contextMetadata.gender ? 'text-red-500' : ''}>
                              Gender: {contextMetadata.gender || '⚠️ Missing'}
                            </span>
                            <span className={!contextMetadata.dob ? 'text-red-500' : ''}>
                              DOB: {contextMetadata.dob || '⚠️ Missing'}
                            </span>
                            <span>Height: {contextMetadata.height || 'Not provided'}</span>
                            <span>Weight: {contextMetadata.weight || 'Not provided'}</span>
                          </div>
                          <p className="text-xs font-medium text-primary pt-1">Quote Parameters (confirm with client):</p>
                          <div className="grid grid-cols-2 gap-1 text-xs text-primary">
                            <span className="text-amber-600">Coverage: $25,000 ⓘ</span>
                            <span>Type: Final Expense</span>
                            <span>Payment: Monthly</span>
                            <span className="text-amber-600">Smoker: Non-Smoker ⓘ</span>
                          </div>
                          <p className="text-[10px] text-amber-600 mt-1">
                            ⓘ InsuraBot will ask to confirm coverage amount and smoker status
                          </p>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full h-auto py-3 px-4 bg-primary hover:bg-primary/90 text-white"
                          onClick={handleQuoteSuggestionClick}
                        >
                          <Calculator className="h-4 w-4 mr-2" />
                          <span className="font-medium">Get Quotes</span>
                        </Button>
                      </div>
                    )}

                    {/* Sample Questions + Watch-a-demo (hide when showing quote suggestion) */}
                    {!isViewerMode && !showQuoteSuggestion && (
                      <div className="space-y-3 mt-4">
                        {/* Watch a demo — always available on the welcome screen */}
                        {demoOffered && <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-left justify-start h-auto py-2.5 px-3 border-primary/30 bg-primary/5 hover:bg-primary/10"
                          onClick={showDemo}
                        >
                          <PlayCircle className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
                          <span className="text-xs font-medium">Watch the full InsuraCentral demo (13 min)</span>
                        </Button>}

                        {sampleQuestions.length > 0 && (
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground px-1 pt-1">
                            <Sparkles className="h-4 w-4" />
                            <span>Try asking:</span>
                          </div>
                        )}
                        <div className="grid grid-cols-1 gap-2">
                          {sampleQuestions.map((q) => (
                            <Button
                              key={q.id}
                              variant="outline"
                              size="sm"
                              className="text-left justify-start h-auto py-2 px-3 whitespace-normal hover:bg-primary/5 hover:border-primary/30"
                              onClick={async () => {
                                // Clear input first, then send the sample question
                                setInput('');
                                await sendMessage(q.question);
                              }}
                            >
                              <span className="text-xs line-clamp-2">{q.question}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(messages && Array.isArray(messages)) && messages.map((message) => {
                  const SourceIcon = getSourceIcon(message.source);
                  return (
                    <div key={message.id} className="space-y-2 min-w-0">
                      {/* User Message bubble.
                          - rounded-2xl + rounded-tr-sm gives a soft "speech
                            bubble" tail on the corner closest to the user.
                          - shadow-sm shadow-primary/20 lifts it slightly
                            off the background so the conversation flow
                            reads more like a chat than two stacked blocks.
                          - base text bumped to text-[15px] (between sm and
                            base) for readability without dwarfing the AI
                            response. */}
                      {/* User bubble. Plain `max-w-[85%]` (no calc — calc()
                          with arbitrary Tailwind values has too many
                          escape-character gotchas). `[overflow-wrap:anywhere]`
                          is the key — it breaks long strings at ANY char,
                          not just word boundaries, so the bubble's text
                          always wraps inside its width cap.
                          Skip the bubble entirely when there's no user prompt
                          — synthetic card-only messages (e.g. the locally
                          injected quote form) have `message.message === ""`
                          and would render an empty bubble at the top right. */}
                      {message.message && (
                        <div className="flex justify-end w-full min-w-0">
                          <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] min-w-0 shadow-sm shadow-primary/20">
                            <p className="text-[15px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{message.message}</p>
                          </div>
                        </div>
                      )}

                      {/* AI Response */}
                      {/* Bot bubble — fills the available width with
                          aggressive wrap on long strings. */}
                      <div className="flex justify-start w-full min-w-0 animate-in fade-in slide-in-from-bottom-1 duration-300">
                        <div className="ai-bot-bubble rounded-xl px-3.5 py-2.5 w-full max-w-full min-w-0 overflow-hidden [overflow-wrap:anywhere]">
                          <div className="flex items-start space-x-2.5 min-w-0">
                            <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-primary">
                              {isMobileShell
                                ? <AnimatedInsuraBotIcon size={20} />
                                : <AnimatedBotIcon size={20} active={!message.response} className="text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              {!message.response ? (
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">Thinking...</span>
                                  <span className="text-xs text-muted-foreground">
                                    {contextType === 'underwriting' ? 'Analyzing underwriting data...' : 'Processing your request...'}
                                  </span>
                                </div>
                              ) : (
                                <div className="min-w-0">
                                  {/* Base size bumped from text-sm to
                                      text-[15px] for readability. Matches
                                      the user-bubble size so a thread
                                      reads as one conversation, not two
                                      different fonts. */}
                                  <div
                                    className="text-[15px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] ai-response-content [&_table]:block [&_table]:overflow-x-auto [&_table]:w-full"
                                    // Intercepts eApp launch links so they
                                    // open in the enrollment iframe/popup
                                    // instead of a browser tab.
                                    onClick={handleChatLinkClick}
                                    // When the Quote Comparison card below
                                    // will render the same table, strip the
                                    // duplicate markdown table from the text
                                    // — keeps intro/footer copy intact.
                                    dangerouslySetInnerHTML={{
                                      __html: cleanAIResponse(message.response, {
                                        stripTables: shouldShowUnderwritingQuoteTable(message.response),
                                      }),
                                    }}
                                  />

                                  {/* Visual Cards */}
                                  <InsuraBotCardRenderer cards={message.cards} response={message.response} />

                                  {/* Enhanced Response Metadata */}
                                  {message.source === 'enhanced_underwriting' && (
                                    <div className="mt-2 space-y-2">
                                      {/* Confidence Score */}
                                      {message.confidence && (
                                        <div className="flex items-center space-x-2">
                                          <Badge variant="outline" className="text-xs">
                                            Confidence: {Math.round(message.confidence * 100)}%
                                          </Badge>
                                          {message.model_used && (
                                            <Badge variant="outline" className="text-xs">
                                              InsuraBot AI
                                            </Badge>
                                          )}
                                        </div>
                                      )}

                                      {/* Evidence Sources */}
                                      {message.evidence_sources && message.evidence_sources.length > 0 && (
                                        <div className="space-y-1">
                                          <p className="text-xs font-medium text-muted-foreground">Sources:</p>
                                          {message.evidence_sources.map((source, idx) => (
                                            <div key={idx} className="text-xs text-muted-foreground">
                                              📄 {source.document_name}
                                              {source.page_reference && ` (Page ${source.page_reference})`}
                                              <span className="ml-2">
                                                Relevance: {Math.round(source.relevance_score * 100)}%
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Auto-detect missing carriers and show request buttons */}
                                  {(() => {
                                    const missingCarriers = parseAIResponseForMissingCarriers(message.response);
                                    if (missingCarriers.length > 0) {
                                      return (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          {missingCarriers.map((carrierName, index) => (
                                            <DocumentRequestButton
                                              key={index}
                                              carrierName={carrierName}
                                              reason={`Need underwriting documentation for ${carrierName} as mentioned in chat`}
                                              className="text-xs"
                                            />
                                          ))}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {/* Show unified underwriting + quotes table when AI response contains tier info OR quote results */}
                                  {/* Always show for InsuraBot - users can ask quote questions without a lead */}
                                  {shouldShowUnderwritingQuoteTable(message.response) && (
                                    <UnderwritingQuoteTable
                                      aiResponse={message.response}
                                      quotes={leadId ? quotes : []} // Only pass quotes if we have a leadId context
                                      isLoadingQuotes={leadId ? isLoadingQuotes : false}
                                      agentCarriers={carriers.map(c => ({
                                        carrierSlug: c.slug || '',
                                        carrierName: c.name || '',
                                        isPreferred: false, // Can be enhanced later if we add preferred carrier support
                                      }))}
                                      showSuggestionsIfRequested={true}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div ref={messagesEndRef} />
            </ScrollArea>

            {/* Input Area */}
            <div className="pt-3 flex-shrink-0">
              {/* File Upload Button - DISABLED per user request */}
              {/* {contextType === 'general' && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInput?.click()}
                    disabled={isUploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                  <input
                    ref={(el) => setFileInput(el)}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <span className="text-xs text-muted-foreground">
                    Supports PDF, DOC, DOCX
                  </span>
                </div>
              )} */}

              {/* Composer — matches CRM house style: soft card, primary
                  border on focus (not a halo), solid primary-gradient send
                  square. No off-theme indigo, no ring halo, no icon
                  translate. The bar height still scales with the widget. */}
              <div className="flex space-x-2 items-end bg-white dark:bg-gray-900 rounded-lg p-1.5 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-150 focus-within:border-primary">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={isViewerMode ? "Chat is unavailable. Please upgrade to continue using InsuraBot." : "Ask me anything about insurance, quotes, underwriting, resources, or materials... (Shift+Enter for new line)"}
                  disabled={isLoading || isViewerMode}
                  style={{
                    minHeight: Math.max(72, Math.min(120, Math.round(widgetSize.height * 0.12))),
                    maxHeight: Math.max(140, Math.min(280, Math.round(widgetSize.height * 0.22))),
                  }}
                  className="flex-1 resize-none !border-0 bg-transparent text-[15px] placeholder:text-gray-400 placeholder:text-[14px] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2"
                  rows={3}
                />

                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading || isViewerMode}
                  aria-label="Send message"
                  className={
                    "h-10 w-10 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 " +
                    (!input.trim() || isLoading || isViewerMode
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-sm shadow-primary/20 hover:from-primary hover:to-primary active:opacity-90")
                  }
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </TabsContent>

          {!isViewerMode && (
            <TabsContent value="history" className="flex-1 flex flex-col min-h-0 mt-0 overflow-hidden">
              {/* Header: label + refresh */}
              <div className="flex items-center justify-between px-1 pb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Recent chats{chatSessions.length ? ` · ${chatSessions.length}` : ''}
                </p>
                <button
                  onClick={() => loadChatSessions()}
                  disabled={loadingSessions}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${loadingSessions ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              {/* Plain scroll container (not Radix ScrollArea) so we own the
                  onScroll event for infinite scroll — loads the next page when
                  the user nears the bottom. loadChatSessions(true) self-guards
                  against overlapping/exhausted loads. */}
              <div
                className="flex-1 overflow-y-auto"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
                    void loadChatSessions(true);
                  }
                }}
              >
              {loadingSessions ? (
                // Skeleton rows while loading — steadier than a "Loading…" flash.
                <div className="space-y-1.5">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
                      <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-muted animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                        <div className="h-2.5 w-1/3 rounded bg-muted animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : chatSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <History className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">No conversations yet</p>
                  <p className="mt-1 text-xs text-muted-foreground">Your past InsuraBot chats show up here.</p>
                  <button
                    onClick={() => setActiveTab('chat')}
                    className="mt-4 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                  >
                    Start a chat
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5 pr-1">
                  {chatSessions.map((session) => (
                    <button
                      key={session.sessionId}
                      onClick={() => loadConversation(session.sessionId)}
                      className="group flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left transition-colors hover:bg-accent"
                    >
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <MessageCircle className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium leading-snug">
                          {session.firstMessagePreview || 'New conversation'}
                        </p>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <span>{formatRelativeTime(session.lastMessageAt || session.createdAt)}</span>
                          <span>·</span>
                          <span>{session.messageCount || 0} msg{(session.messageCount || 0) === 1 ? '' : 's'}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}

                  {/* Infinite-scroll footer */}
                  {loadingMoreSessions && (
                    <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more…
                    </div>
                  )}
                  {!hasMoreSessions && chatSessions.length > 0 && (
                    <p className="py-3 text-center text-[11px] text-muted-foreground">That&apos;s all your conversations</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
          )}

          {!isViewerMode && (
            <TabsContent value="settings" className="flex-1 flex flex-col min-h-0 mt-0 overflow-y-auto">
            <div className="space-y-4 pb-6">
            {/* Chat Management */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Chat Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={clearMessages} className="w-full">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Clear Chat
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => loadCarriers()} className="w-full">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Request Carrier Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Carrier Requests
                </CardTitle>
                <CardDescription className="text-xs">
                  Request new carriers or submit underwriting data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={isRequestModalOpen} onOpenChange={(open) => {
                  if (!open) resetRequestForm();
                  setIsRequestModalOpen(open);
                }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="default"
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Request Carrier
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        Carrier Request
                      </DialogTitle>
                      <DialogDescription>
                        Request a new carrier or submit underwriting data for InsuraBot.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                      {/* Request Type Selection */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Request Type</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setRequestType("add_carrier")}
                            disabled={isSubmittingRequest}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              requestType === "add_carrier"
                                ? "border-primary bg-primary/5"
                                : "border-gray-200 hover:border-primary/30 bg-white"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Plus className={`h-4 w-4 ${requestType === "add_carrier" ? "text-primary" : "text-gray-500"}`} />
                              <div>
                                <p className="font-medium text-xs">Add Carrier</p>
                                <p className="text-[10px] text-muted-foreground">Request new carrier</p>
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => setRequestType("underwriting_data")}
                            disabled={isSubmittingRequest}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              requestType === "underwriting_data"
                                ? "border-primary bg-primary/5"
                                : "border-gray-200 hover:border-primary/30 bg-white"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <FileText className={`h-4 w-4 ${requestType === "underwriting_data" ? "text-primary" : "text-gray-500"}`} />
                              <div>
                                <p className="font-medium text-xs">Submit Data</p>
                                <p className="text-[10px] text-muted-foreground">Upload guidelines</p>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Carrier Name Input */}
                      <div className="space-y-1.5">
                        <Label htmlFor="carrierNameInsuraBot" className="text-xs">
                          Carrier Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="carrierNameInsuraBot"
                          placeholder="e.g., ABC Life Insurance"
                          value={newCarrierName}
                          onChange={(e) => setNewCarrierName(e.target.value)}
                          disabled={isSubmittingRequest}
                          className="h-9 text-sm"
                        />
                      </div>

                      {/* Reason/Notes Input */}
                      <div className="space-y-1.5">
                        <Label htmlFor="requestReasonInsuraBot" className="text-xs">
                          {requestType === "add_carrier" ? "Reason" : "Notes"} <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="requestReasonInsuraBot"
                          placeholder={requestType === "add_carrier"
                            ? "Why do you need this carrier?"
                            : "Describe the underwriting data..."
                          }
                          value={requestReason}
                          onChange={(e) => setRequestReason(e.target.value)}
                          disabled={isSubmittingRequest}
                          className="min-h-[80px] text-sm"
                        />
                      </div>

                      {/* PDF Upload Section - Only for underwriting data */}
                      {requestType === "underwriting_data" && (
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">
                            Upload PDF <span className="text-muted-foreground">(Optional)</span>
                          </Label>

                          {/* OAuth Connection Status */}
                          {checkingOAuthConnection ? (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                              <Loader2 className="h-3 w-3 animate-spin text-gray-500" />
                              <span className="text-xs text-gray-500">Checking email connection...</span>
                            </div>
                          ) : oauthConnection ? (
                            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                              <Mail className="h-3 w-3 text-green-600" />
                              <span className="text-xs text-green-700">
                                Connected: {oauthConnection.email}
                              </span>
                            </div>
                          ) : (
                            <Alert className="bg-amber-50 border-amber-200 py-2">
                              <AlertCircle className="h-3 w-3 text-amber-600" />
                              <AlertDescription className="text-xs text-amber-800">
                                <button
                                  type="button"
                                  onClick={handleConnectEmail}
                                  className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
                                >
                                  <Link2 className="h-3 w-3" />
                                  Connect Gmail or Outlook
                                </button>
                                {" "}to upload PDFs via your email.
                              </AlertDescription>
                            </Alert>
                          )}

                          {/* File Upload Drop Zone - Only show if OAuth is connected */}
                          {oauthConnection && (
                            <>
                              <input
                                type="file"
                                ref={fileUploadInputRef}
                                onChange={handleFileInputChangeForRequest}
                                accept=".pdf,application/pdf"
                                className="hidden"
                                disabled={isSubmittingRequest}
                              />

                              {!selectedFile ? (
                                <div
                                  onDragOver={handleFileDragOver}
                                  onDragLeave={handleFileDragLeave}
                                  onDrop={handleFileDrop}
                                  onClick={() => fileUploadInputRef.current?.click()}
                                  className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                                    isFileDragging
                                      ? "border-primary bg-primary/5"
                                      : "border-gray-300 hover:border-primary/40 hover:bg-gray-50"
                                  }`}
                                >
                                  <Upload className={`h-6 w-6 mx-auto mb-1 ${isFileDragging ? "text-primary" : "text-gray-400"}`} />
                                  <p className="text-xs font-medium text-gray-700">
                                    {isFileDragging ? "Drop PDF here" : "Click or drag PDF"}
                                  </p>
                                  <p className="text-[10px] text-gray-500">Max 10MB</p>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 p-2 bg-primary/5 border border-primary/20 rounded-lg">
                                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-xs text-gray-900 truncate">{selectedFile.name}</p>
                                    <p className="text-[10px] text-gray-500">
                                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeSelectedFile();
                                    }}
                                    disabled={isSubmittingRequest}
                                    className="h-6 w-6 p-0 text-gray-500 hover:text-red-600"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={resetRequestForm}
                          disabled={isSubmittingRequest}
                          className="flex-1 h-9"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRequestCarrier}
                          disabled={isSubmittingRequest || !newCarrierName.trim() || !requestReason.trim()}
                          className="flex-1 h-9 bg-primary hover:bg-primary/90"
                        >
                          {isSubmittingRequest ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Mail className="h-3 w-3 mr-1.5" />
                              Submit
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Analytics */}
            {enableAdminFeatures && analytics && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    AI Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-2 bg-primary/5 rounded-lg">
                      <div className="text-xl font-bold text-primary">{analytics.total_conversations}</div>
                      <div className="text-[10px] text-primary">Conversations</div>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-700">{analytics.enhanced_underwriting_usage}</div>
                      <div className="text-[10px] text-green-600">Underwriting</div>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">General AI</span>
                      <span className="font-medium">{analytics.general_ai_usage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Enhanced UW</span>
                      <span className="font-medium">{analytics.enhanced_underwriting_usage}</span>
                    </div>
                  </div>

                  {analytics.top_carriers_queried.length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t">
                      <div className="text-xs font-medium">Top Carriers</div>
                      {analytics.top_carriers_queried.slice(0, 3).map((carrier, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="capitalize text-muted-foreground">{carrier.carrier_slug.replace(/-/g, ' ')}</span>
                          <span className="font-medium">{carrier.query_count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </div>
          </TabsContent>
          )}

          {/* InsuraCentral tab — app navigation + help, in the same bot UI. The
              Client tab (left) answers client/underwriting/quote questions; this
              tab (right) is everything InsuraCentral-platform related. */}
          {/* Helper tab — the full InsuraHelp knowledge-base chat, embedded.
              Same page-aware smart quick actions, streaming answers, navigation
              commands, tours and co-browse "AI Assist" as the old floating "?"
              widget (which is now retired). The Client tab (left) answers
              client / underwriting / quote questions; this tab (right) is
              everything InsuraCentral-platform related. The -mx-4 bleeds the
              panel to the shell edges (the Tabs wrapper adds px-4). */}
          <TabsContent value="insuracentral" className="flex-1 flex flex-col min-h-0 mt-0 -mx-4 overflow-hidden">
            <InsuraHelpPanel
              isOpen={activeTab === 'insuracentral'}
              onClose={() => onOpenChange(false)}
              embedded
            />
          </TabsContent>
        </Tabs>
      </div>,
      document.body
    );
};
